import { useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { PlzOrte } from "@kundeninfo/api-client";
import { Auswahl, Eingabe, Hinweis } from "@kundeninfo/ui/me-design";
import { formatDatum, landName } from "../format.js";
import "./Ortszeile.css";

/**
 * DIE ORTSZEILE (Spec „Adresszeile", E400, E403, R17–R22) — Land, PLZ und Ort in EINER Zeile,
 * EIN Baustein für alle Adressmasken (Adresspanel, Kunde anlegen, Kunden-Stammdaten,
 * Verwaltung › Unternehmen). Er kennt keine Fachregel: Ob eine PLZ gültig ist, prüft der Server
 * (E401), sein Befund kommt über `fehler` ans Feld. Der Baustein schlägt nur vor:
 *
 * - Bei DE und genau fünf Ziffern fragt er das PLZ-Verzeichnis (je PLZ einmal je Maske). Ein
 *   Fehler der Abfrage bleibt still — Lenkung, kein Pflichtweg.
 * - Ist der Ort leer oder steht dort noch SEIN letzter Vorschlag, setzt er den ersten Ort der
 *   Rangfolge der API-Antwort (meiste Straßen in der PLZ, dann Einwohner). Einen getippten oder
 *   gespeicherten Ort überschreibt er nie — auch nicht mit einer späten Antwort für eine
 *   überholte PLZ. Kennt das Verzeichnis die neue PLZ nicht (leere Liste), leert er seinen eigenen
 *   Vorschlag; scheitert die Abfrage, bleibt er stehen (unbekannt, ob er falsch ist).
 * - Das tut er erst nach einer Eingabe des Benutzers in PLZ oder Land: Beim Einbau mit
 *   vorhandenen Werten bleibt der Ort, wie er ist — sonst gälte die Maske schon beim Öffnen als
 *   geändert und fragte beim Schließen „verwerfen?". Der Hinweis unten darf trotzdem schon beim
 *   Öffnen erscheinen: Er ändert keinen Wert.
 * - Passt ein Ort nicht zur PLZ, steht ein Hinweis da (R21: Hinweis, kein Fehler).
 * - Die Orte der PLZ stehen als Vorschläge im Ort-Feld selbst (me-design W1: `me-eingabe` mit
 *   `<option>`-Kindern, Kombifeld; ↓/↑ oder Klick öffnet die Liste mit allen Orten). Eine Auswahl
 *   unter der Zeile gibt es nicht. Das Land zeigt geschlossen das Kürzel, in der Liste „DE – Deutschland“
 *   (me-design W2: `data-kurz` an `me-auswahl`).
 * - `gesperrt`: alle Felder gesperrt, kein Vorschlag, keine Abfrage.
 *
 * Der Einbauort setzt einen `key` aus Datensatz UND Fassung (Version): Der innere Stand —
 * „mein letzter Vorschlag", „der Benutzer hat getippt", die gemerkten Antworten — gehört zu EINEM
 * Stand des Datensatzes und überlebt Neuladen, Speichern und Datensatzwechsel nicht.
 */

export type OrtszeileWerte = { country: string; postalCode: string; city: string };
export type OrtszeileFeld = keyof OrtszeileWerte;

export type OrtszeileProps = {
  /** Kennungen `${praefix}-country|postalCode|city` — dieselben Feldnamen wie die 422-Befunde. */
  praefix: string;
  werte: OrtszeileWerte;
  /** Länderkürzel vom Server, in seiner Reihenfolge; der gewählte Wert bleibt immer wählbar. */
  laender: readonly string[];
  fehler?: Readonly<Record<string, string | undefined>>;
  onAenderung(teil: Partial<OrtszeileWerte>): void;
  /** `client.listPlzOrte` — fehlt sie, schlägt die Zeile nichts vor. */
  plzOrte?: ((plz: string) => Promise<PlzOrte>) | undefined;
  /** Sperre der Maske (Speichern läuft, fremder Stand): alle Felder gesperrt, kein
   *  Vorschlag, keine Abfrage — auch keine Antwort, die erst während der Sperre eintrifft. */
  gesperrt?: boolean | undefined;
  /** Je Feld ein `Fund` im Slot `fund` (me-design 137, etwa Vorschläge der Anreicherung): „Übernehmen“ setzt den Wert
   *  über das Feld selbst und kommt als gewöhnliche Eingabe über `onAenderung` an. */
  funde?: Partial<Record<OrtszeileFeld, ReactNode>> | undefined;
};

const FUENF_ZIFFERN = /^[0-9]{5}$/;

function normal(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLocaleLowerCase("de");
}

/** Beginnt `lang` mit `kurz` und geht danach nach Leerzeichen, „-", „,", „/" oder „(" weiter? */
const erweitert = (lang: string, kurz: string): boolean =>
  lang.startsWith(kurz) && /^[ \-,/(]/.test(lang.slice(kurz.length));

/** Passt der Ort zum Namen aus dem Verzeichnis? Gleich — oder einer beginnt mit dem anderen und geht
 *  nach Leerzeichen, „-", „,", „/" oder „(" weiter: der Ort mit Zusatz („Senftenberg OT Brieske" zu
 *  „Senftenberg") wie die Kurzform („Frankfurt" zu „Frankfurt am Main", Entscheid 420). */
export function ortPasst(ort: string, name: string): boolean {
  const o = normal(ort);
  const n = normal(name);
  return o === n || erweitert(o, n) || erweitert(n, o);
}

/** „DE – Deutschland“ — ohne bekannten Namen nur das Kürzel. */
export function landLangtext(code: string): string {
  const name = landName(code);
  return name === code ? code : `${code} – ${name}`;
}

export function Ortszeile(p: OrtszeileProps): ReactElement {
  const { werte } = p;
  const [treffer, setTreffer] = useState<PlzOrte | null>(null);
  const gemerkt = useRef(new Map<string, PlzOrte>());
  /** Der Ort, den die Zeile zuletzt SELBST gesetzt hat — nur ihn darf ein neuer Vorschlag ersetzen. */
  const vorschlag = useRef<string | null>(null);
  const aktuell = useRef(werte);
  aktuell.current = werte;
  const melde = useRef(p.onAenderung);
  melde.current = p.onAenderung;
  const abfrage = useRef(p.plzOrte);
  abfrage.current = p.plzOrte;
  const gesperrt = p.gesperrt === true;
  /** Ref statt Abhängigkeit: Entsperren löst keine Abfrage und keinen Vorschlag aus. */
  const sperre = useRef(gesperrt);
  sperre.current = gesperrt;
  /** Hat der Benutzer PLZ oder Land angefasst? Erst dann darf die Zeile den Ort füllen. */
  const eingegeben = useRef(false);

  const plz = werte.postalCode.trim();
  const istDe = werte.country === "DE";
  const fragen = istDe && FUENF_ZIFFERN.test(plz);

  useEffect(() => {
    if (!fragen || sperre.current) return;
    let abgebrochen = false;
    const uebernimm = (antwort: PlzOrte): void => {
      if (abgebrochen) return;
      gemerkt.current.set(plz, antwort);
      setTreffer(antwort);
      const jetzt = aktuell.current;
      if (!eingegeben.current || sperre.current
        || jetzt.country !== "DE" || jetzt.postalCode.trim() !== plz) return;
      const ort = jetzt.city.trim();
      if (ort !== "" && ort !== vorschlag.current) return;
      // Kennt das Verzeichnis die PLZ nicht, geht der Vorschlag der alten PLZ mit seiner Grundlage.
      const neu = antwort.orte[0]?.name ?? "";
      vorschlag.current = neu === "" ? null : neu;
      if (ort !== neu) melde.current({ city: neu });
    };
    const bekannt = gemerkt.current.get(plz);
    if (bekannt !== undefined) {
      uebernimm(bekannt);
    } else {
      Promise.resolve()
        .then(() => abfrage.current?.(plz))
        .then((antwort) => { if (antwort !== undefined) uebernimm(antwort); })
        .catch(() => { /* Lenkung, kein Pflichtweg (R19) */ });
    }
    return () => { abgebrochen = true; };
  }, [fragen, plz]);

  const antwort = fragen && treffer?.plz === plz ? treffer : null;
  const orte = antwort?.orte ?? [];
  const ort = werte.city.trim();
  const unpassend = orte.length > 0 && ort !== "" && !orte.some((o) => ortPasst(ort, o.name));
  const unbekannt = antwort !== null && orte.length === 0 && antwort.stand !== null;
  const quelle = antwort?.stand == null ? undefined
    : "PLZ-Verzeichnis: © OpenStreetMap-Mitwirkende (ODbL), aufbereitet von OpenPLZ API · Einwohner: "
      + "© Statistisches Bundesamt (Destatis), Gemeindeverzeichnis"
      + (antwort.gebietsstand === null ? "" : `, Gebietsstand ${formatDatum(antwort.gebietsstand)}`)
      + ` · Stand ${formatDatum(antwort.stand)}`;
  const laender = p.laender.includes(werte.country) ? p.laender : [werte.country, ...p.laender];
  const f = p.fehler ?? {};
  const funde = p.funde ?? {};

  return (
    <div className="oz">
      <div className="oz__zeile">
        <Auswahl id={`${p.praefix}-country`} name={`${p.praefix}-country`} beschriftung="Land"
          wert={werte.country} gesperrt={gesperrt} fund={funde.country}
          {...(f["country"] !== undefined ? { fehler: f["country"] } : {})}
          onGeaendert={(d) => { eingegeben.current = true; p.onAenderung({ country: d.wert }); }}>
          {laender.map((code) => <option key={code} value={code} data-kurz={code}>{landLangtext(code)}</option>)}
        </Auswahl>
        <Eingabe id={`${p.praefix}-postalCode`} name={`${p.praefix}-postalCode`} beschriftung="PLZ"
          art="text" wert={werte.postalCode} gesperrt={gesperrt} fund={funde.postalCode}
          inputMode={istDe ? "numeric" : "text"} maxLength={istDe ? 5 : 10}
          {...(f["postalCode"] !== undefined ? { fehler: f["postalCode"] } : {})}
          onGeaendert={(d) => { eingegeben.current = true; p.onAenderung({ postalCode: d.wert }); }} />
        <Eingabe className="oz__ort" id={`${p.praefix}-city`} name={`${p.praefix}-city`}
          beschriftung="Ort" art="text" wert={werte.city} gesperrt={gesperrt} fund={funde.city}
          {...(quelle !== undefined && (orte.length > 1 || unpassend) ? { hilfe: quelle } : {})}
          {...(f["city"] !== undefined ? { fehler: f["city"] } : {})}
          onGeaendert={(d) => p.onAenderung({ city: d.wert })}
          // Ein übernommener Vorschlag, der den Ort ändert, ist wieder „mein Vorschlag“ (R19). Wer nur den
          // eingetragenen Ort bestätigt (↓ + Enter/Tab, Klick), schützt ihn weiter — auch in anderer Schreibweise
          // oder Kurzform (W1c, `ortPasst`); ein getippter Ort nie. Tragend: zwischen `me-geaendert` und
          // `me-vorschlag-gewaehlt` rendert React nicht (me-eingabe löst beide in `vorschlagUebernehmen_` nacheinander
          // aus), `aktuell` hält den Ort also noch vor der Übernahme — erst das Rendern schreibt es fort.
          onVorschlagGewaehlt={(d) => { if (!ortPasst(d.wert, aktuell.current.city)) vorschlag.current = d.wert; }}>
          {orte.map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
        </Eingabe>
      </div>
      {unpassend && (
        <Hinweis art="warnung">
          {`„${ort}“ passt laut PLZ-Verzeichnis nicht zur PLZ ${plz}. Speichern geht trotzdem — bitte prüfen.`}
        </Hinweis>
      )}
      {unbekannt && (
        <Hinweis art="info">
          {`Die PLZ ${plz} steht nicht im PLZ-Verzeichnis — Großkunden- und Postfach-Postleitzahlen fehlen `
            + "dort. Bitte prüfen."}
          {quelle !== undefined && <span className="oz__quelle">{quelle}</span>}
        </Hinweis>
      )}
    </div>
  );
}
